import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  getManageTemplateList,
  deleteManageTemplate,
  getProductions,
  getExports,
  securityGroupDefultSaveTemplate,
  getProductionBasicInfo,
  saveProductionBasicInfo,
  getSecurityGroup,
  getProductionsAndExportsOptions,
  createSet,
  getProductionBasicInfoInEditMode,
  getDatComponent,
  getTemplateDatComponent,
  getNativeComponent,
  getTemplateNativeComponent,
  getTextComponent,
  getTemplateTextComponent,
  getMp3FileComponent,
  getTemplateMp3FileComponent,
  getTranslationComponent,
  getTemplateTranslationComponent,
  getTiffComponent,
  getTemplateTiffComponent,
  getProductionComponents,
  getTemplateComponents,
  saveProductionComponents,
  createProductionTemplate,
  deleteProduction,
  uploadFile,
  getNumberingSortingView,
  getTemplateNumberingSortingDetails,
  saveNumberingSorting,
  getBatesNext,
  getAssignments,
  getDocumentSelectionView,
  saveDocumentsSelection,
  getProductionGuardView,
  getTemplateGuardDetails,
  saveProductionGuardView,
  getProductionGuardMatchResults,
  updateSet,
  deleteSet,
  getProductionSetInfo,
  downloadProduction,
  getNativeErrorList,
  getErrorList,
  exportErrorList,
  getReProductionErrorList,
  exportReProductionErrorList,
  getProductLocationView,
  validateDestination,
  createDestination,
  saveProductionLocation,
  getSummaryView,
  saveSummary,
  getGenerateView,
  getGenerateStatus,
  getExportBatesRange,
  executeGenerate,
  executePlaceholdering,
  confirmProduction,
  getShareableLinks,
  getConfirmView,
  getProductionPreview,
  getProductionPreviewFile,
  uploadSeedFile,
  uploadReProductionSeedFile,
  validateDownload,
  getSeedPath,
  getReProductionSeedPath,
  seedPageCount,
  reProductionSeedPageCount,
  getValidateDownloadURI,
  cancelProduction,
  retryCancelProduction,
  getReProductionConfirmView,
  confirmReProduction,
} from '../../api/endpoints/productionApi';
import {
  DeleteManageTemplateResponse,
  GetManageTemplatesRequestParam,
  GetManageTemplatesResponse,
  GetProductionBasicInfoRequestParam,
  GetProductionBasicInfoResponse,
  GetProductionTilesRequestParam,
  GetSecurityRes,
  IGetProductionsResponse,
  SaveProductionBasicInfoPayload,
  SaveProductionBasicInfoResponse,
  ProductionExportOptionsResponse,
  ICreateSetPayload,
  ICreateSetResponse,
  IUpdateSetPayload,
  IUpdateSetResponse,
  IDeleteSetPayload,
  IDeleteSetResponse,
  GetProductionSetInfoRequestParams,
  GetProductionSetInfoResponse,
  EditViewProductionBasicInfoResponse,
  GetDatComponentRequestParam,
  GetDatComponentResponse,
  GetTemplateDatComponentResponse,
  GetNativeComponentRequestParam,
  GetNativeComponentResponse,
  GetTextComponentRequestParam,
  GetTextComponentResponse,
  GetTemplateTextComponentResponse,
  GetMp3FileComponentRequestParam,
  GetMp3FileComponentResponse,
  GetTranslationComponentRequestParam,
  GetTranslationComponentResponse,
  GetTiffComponentRequestParam,
  GetTiffComponentResponse,
  GetProductionComponentsRequestParam,
  GetProductionComponentsResponse,
  GetTemplateDetailsRequestParam,
  GetTemplateNumberingSortingDetailsResponse,
  GetNumberingSortingViewResponse,
  SaveProductionComponentsPayload,
  SaveProductionComponentsResponse,
  CreateProductionTemplatePayload,
  CreateProductionTemplateResponse,
  DeleteProductionResponse,
  Assignment,
  GetDocumentSelectionViewRequestParam,
  GetDocumentSelectionViewResponse,
  SaveDocumentsSelectionPayload,
  SaveDocumentsSelectionResponse,
  GetProductionGuardViewRequestParam,
  GetProductionGuardViewResponse,
  SaveProductionGuardViewPayload,
  SaveProductionGuardViewResponse,
  GetProductionGuardMatchResultsPayload,
  GetProductionGuardMatchResultsResponse,
  NextBatesResponse,
  DownloadProductionResponse,
  GetNativeErrorListRequestPayload,
  GetNativeErrorListResponse,
  GetErrorListRequestPayload,
  GetErrorListResponse,
  ExportErrorListRequestParams,
  ExportErrorListResponse,
  GetProductLocationViewRequestParam,
  GetProductLocationViewResponse,
  ValidateDestinationRequestParams,
  ValidateDestinationResponse,
  CreateDestinationRequestBody,
  CreateDestinationResponse,
  SaveProductionLocationRequestBody,
  SaveProductionLocationResponse,
  GetSummaryViewRequestParam,
  GetSummaryViewResponse,
  SaveSummaryRequestBody,
  SaveSummaryResponse,
  GetGenerateViewRequestParam,
  GetGenerateViewResponse,
  GetGenerateStatusRequestParam,
  GetGenerateStatusResponse,
  GetExportBatesRangeRequestParam,
  GetExportBatesRangeResponse,
  ExecuteGenerateRequestBody,
  ExecuteGenerateResponse,
  ExecutePlaceholderingRequestBody,
  ExecutePlaceholderingResponse,
  ConfirmProductionRequestParams,
  ConfirmProductionResponse,
  GetShareableLinksRequestParams,
  GetShareableLinksResponse,
  GetConfirmViewRequestParam,
  GetConfirmViewResponse,
  GetProductionPreviewRequestParam,
  GetProductionPreviewResponse,
  GetProductionPreviewFileRequestParam,
  GetProductionPreviewFileResponse,
  UploadSeedFileResponse,
  ValidateDownloadRequest,
  ValidateDownloadResponse,
  GetSeedPathRequestParam,
  GetSeedPathResponse,
  SeedPageCountRequest,
  SeedPageCountResponse,
  ValidateDownloadURIRequest,
  ValidateDownloadURIResponse,
  CancelProductionResponse,
  CancelProductionRequest,
} from '../../api/types/production.type';
import {
  IGetExportsResponse,
  GetExportsTilesRequestParam,
} from '../../api/types/export.type';
import {GetReProductionConfirmViewResponse} from '../../api/types/reproduction.type';

export const useGetManageTemplates = (
  queryParams: GetManageTemplatesRequestParam
) => {
  return useQuery<GetManageTemplatesResponse, Error>({
    queryKey: ['manageTemplates', queryParams],
    queryFn: () => getManageTemplateList(queryParams),
  });
};

export const useDeleteManageTemplate = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<DeleteManageTemplateResponse, Error, number>({
    mutationFn: deleteManageTemplate,
    mutationKey: ['deleteManageTemplate'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useProductions = (
  queryParams: GetProductionTilesRequestParam,
  enabled: boolean
) => {
  return useQuery<IGetProductionsResponse, Error>({
    queryKey: ['productions', queryParams],
    queryFn: () => getProductions(queryParams),
    retry: 2,
    enabled,
  });
};

export const useExports = (
  queryParams: GetExportsTilesRequestParam,
  enabled: boolean
) => {
  return useQuery<IGetExportsResponse, Error>({
    queryKey: ['exports', queryParams],
    queryFn: () => getExports(queryParams),
    retry: 2,
    enabled,
  });
};

export const useViewHomeSecurityGroup = () => {
  return useQuery<GetSecurityRes, Error>({
    queryKey: ['viewHomeSecurityGroup'],
    queryFn: () => getSecurityGroup(),
    retry: 2,
  });
};

export const useProductionsAndExportsOptions = (
  selectedSecurityGrpId: number,
  enabled: boolean
) => {
  return useQuery<ProductionExportOptionsResponse, Error>({
    queryKey: ['productionsAndExportsOptions', selectedSecurityGrpId],
    queryFn: () => getProductionsAndExportsOptions(selectedSecurityGrpId),
    enabled,
  });
};

export const useAssignmentsList = (selectedSecurityGrpId: number) => {
  return useQuery<Assignment[], Error>({
    queryKey: [
      'productionsAndExportsOptions',
      selectedSecurityGrpId,
      'assignments',
    ],
    queryFn: () => getAssignments(selectedSecurityGrpId),
  });
};

export const useGetProductionSetInfo = (
  params: GetProductionSetInfoRequestParams,
  enabled: boolean = true
) => {
  return useQuery<GetProductionSetInfoResponse, Error>({
    queryKey: ['productionSetInfo', params],
    queryFn: () => getProductionSetInfo(params),
    enabled,
    retry: 2,
  });
};

export const useCreateProductionAndExportSet = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICreateSetResponse, Error, ICreateSetPayload>({
    mutationFn: createSet,
    mutationKey: ['createSet'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateProductionSet = (
  onSuccess?: (data: IUpdateSetResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IUpdateSetResponse, Error, IUpdateSetPayload>({
    mutationFn: updateSet,
    mutationKey: ['updateProductionSet'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSecurityGroupDefaultSaveTemplate = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation({
    mutationFn: securityGroupDefultSaveTemplate,
    mutationKey: ['securityGroupDefultSaveTemplate'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProductionBasicInfo = (
  queryParams: GetProductionBasicInfoRequestParam
) => {
  return useQuery<GetProductionBasicInfoResponse, Error>({
    queryKey: ['getProductionBasicInfo', queryParams],
    queryFn: () => getProductionBasicInfo(queryParams),
  });
};

export const useSaveProductionBasicInfo = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveProductionBasicInfoResponse,
    Error,
    SaveProductionBasicInfoPayload
  >({
    mutationFn: saveProductionBasicInfo,
    mutationKey: ['saveProductionBasicInfo'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProductionBasicInfoInEditMode = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<EditViewProductionBasicInfoResponse, Error, string>({
    mutationFn: getProductionBasicInfoInEditMode,
    mutationKey: ['getProductionBasicInfoInEditMode'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDatComponent = (
  queryParams: GetDatComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetDatComponentResponse, Error>({
    queryKey: ['datComponent', queryParams],
    queryFn: () => getDatComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetNativeComponent = (
  queryParams: GetNativeComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetNativeComponentResponse, Error>({
    queryKey: ['nativeComponent', queryParams],
    queryFn: () => getNativeComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTextComponent = (
  queryParams: GetTextComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTextComponentResponse, Error>({
    queryKey: ['textComponent', queryParams],
    queryFn: () => getTextComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetMp3FileComponent = (
  queryParams: GetMp3FileComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetMp3FileComponentResponse, Error>({
    queryKey: ['mp3FileComponent', queryParams],
    queryFn: () => getMp3FileComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTranslationComponent = (
  queryParams: GetTranslationComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTranslationComponentResponse, Error>({
    queryKey: ['translationComponent', queryParams],
    queryFn: () => getTranslationComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTiffComponent = (
  queryParams: GetTiffComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTiffComponentResponse, Error>({
    queryKey: ['tiffComponent', queryParams],
    queryFn: () => getTiffComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateTiffComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTiffComponentResponse, Error>({
    queryKey: ['templateTiffComponent', queryParams],
    queryFn: () => getTemplateTiffComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateDatComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTemplateDatComponentResponse, Error>({
    queryKey: ['templateDatComponent', queryParams],
    queryFn: () => getTemplateDatComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateNativeComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetNativeComponentResponse, Error>({
    queryKey: ['templateNativeComponent', queryParams],
    queryFn: () => getTemplateNativeComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateTranslationComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTranslationComponentResponse, Error>({
    queryKey: ['templateTranslationComponent', queryParams],
    queryFn: () => getTemplateTranslationComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateMp3FileComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetMp3FileComponentResponse, Error>({
    queryKey: ['templateMp3FileComponent', queryParams],
    queryFn: () => getTemplateMp3FileComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateTextComponent = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTemplateTextComponentResponse, Error>({
    queryKey: ['templateTextComponent', queryParams],
    queryFn: () => getTemplateTextComponent(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetProductionComponents = (
  queryParams: GetProductionComponentsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetProductionComponentsResponse, Error>({
    queryKey: ['productionComponents', queryParams],
    queryFn: () => getProductionComponents(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateComponents = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetProductionComponentsResponse, Error>({
    queryKey: ['templateComponents', queryParams],
    queryFn: () => getTemplateComponents(queryParams),
    enabled,
    retry: 2,
  });
};

export const useSaveProductionComponents = (
  onSuccess?: (data: SaveProductionComponentsResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveProductionComponentsResponse,
    Error,
    SaveProductionComponentsPayload
  >({
    mutationFn: saveProductionComponents,
    mutationKey: ['saveProductionComponents'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateProductionTemplate = (
  onSuccess?: (data: CreateProductionTemplateResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    CreateProductionTemplateResponse,
    Error,
    CreateProductionTemplatePayload
  >({
    mutationFn: createProductionTemplate,
    mutationKey: ['createProductionTemplate'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteProduction = (
  onSuccess?: (data: DeleteProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<DeleteProductionResponse, Error, string>({
    mutationFn: deleteProduction,
    mutationKey: ['deleteProduction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCancelProduction = (
  onSuccess?: (data: CancelProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<CancelProductionResponse, Error, CancelProductionRequest>({
    mutationFn: cancelProduction,
    mutationKey: ['cancelProduction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRetryCancelProduction = (
  onSuccess?: (data: CancelProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<CancelProductionResponse, Error, CancelProductionRequest>({
    mutationFn: retryCancelProduction,
    mutationKey: ['retryCancelProduction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteProductionSet = (
  onSuccess?: (data: IDeleteSetResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDeleteSetResponse, Error, IDeleteSetPayload>({
    mutationFn: deleteSet,
    mutationKey: ['deleteProductionSet'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUploadFile = (
  onSuccess?: (data: {message: string}) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<{message: string}, Error, File>({
    mutationFn: uploadFile,
    mutationKey: ['uploadFile'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetNumberingSortingView = (
  queryParams: {SecurityGroupId: number; productionId: number},
  enabled: boolean = true
) => {
  return useQuery<GetNumberingSortingViewResponse, Error>({
    queryKey: ['numberingSortingView', queryParams],
    queryFn: () => getNumberingSortingView(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateNumberingSortingDetails = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTemplateNumberingSortingDetailsResponse, Error>({
    queryKey: ['templateNumberingSortingDetails', queryParams],
    queryFn: () => getTemplateNumberingSortingDetails(queryParams),
    enabled,
    retry: 2,
  });
};

export const useSaveNumberingSorting = (
  onSuccess?: (data: {message: string}) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    {message: string},
    Error,
    {
      productionnumberingSettings: any;
      productionSortingSettings: any;
      isMarkComplete: boolean;
      productionId: number;
      productionSecurityGroupId: number;
    }
  >({
    mutationFn: saveNumberingSorting,
    mutationKey: ['saveNumberingSorting'],
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ['numberingSortingView']});
      queryClient.invalidateQueries({
        queryKey: ['templateNumberingSortingDetails'],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetBatesNext = (
  queryParams: {SecurityGroupId: number; productionId: number},
  enabled: boolean = true
) => {
  return useQuery<NextBatesResponse, Error>({
    queryKey: ['batesNext', queryParams],
    queryFn: () => getBatesNext(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetDocumentSelectionView = (
  queryParams: GetDocumentSelectionViewRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetDocumentSelectionViewResponse, Error>({
    queryKey: ['documentSelectionView', queryParams],
    queryFn: () => getDocumentSelectionView(queryParams),
    enabled,
    retry: 2,
  });
};

export const useSaveDocumentsSelection = (
  onSuccess?: (data: SaveDocumentsSelectionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveDocumentsSelectionResponse,
    Error,
    SaveDocumentsSelectionPayload
  >({
    mutationFn: saveDocumentsSelection,
    mutationKey: ['saveDocumentsSelection'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProductionGuardView = (
  queryParams: GetProductionGuardViewRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetProductionGuardViewResponse, Error>({
    queryKey: ['productionGuardView', queryParams],
    queryFn: () => getProductionGuardView(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTemplateGuardDetails = (
  queryParams: GetTemplateDetailsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetProductionGuardViewResponse, Error>({
    queryKey: ['templateGuardDetails', queryParams],
    queryFn: () => getTemplateGuardDetails(queryParams),
    enabled,
    retry: 2,
  });
};

export const useSaveProductionGuardView = (
  onSuccess?: (data: SaveProductionGuardViewResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveProductionGuardViewResponse,
    Error,
    SaveProductionGuardViewPayload
  >({
    mutationFn: saveProductionGuardView,
    mutationKey: ['saveProductionGuardView'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProductionGuardMatchResults = (
  onSuccess?: (data: GetProductionGuardMatchResultsResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GetProductionGuardMatchResultsResponse,
    Error,
    GetProductionGuardMatchResultsPayload
  >({
    mutationFn: getProductionGuardMatchResults,
    mutationKey: ['getProductionGuardMatchResults'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDownloadProduction = (
  onSuccess?: (data: DownloadProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DownloadProductionResponse,
    Error,
    {sourcePath: string; fileName: string}
  >({
    mutationFn: (params) => downloadProduction(params),
    mutationKey: ['downloadProduction'],
    onSuccess: (data) => {
      // Create download link and trigger download
      const url = window.URL.createObjectURL(data.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetNativeErrorList = (
  payload: GetNativeErrorListRequestPayload,
  enabled: boolean = false
) => {
  return useQuery<GetNativeErrorListResponse, Error>({
    queryKey: ['getNativeErrorList', payload],
    queryFn: () => getNativeErrorList(payload),
    enabled,
  });
};

export const useGetErrorList = (
  payload: GetErrorListRequestPayload,
  enabled: boolean = false
) => {
  return useQuery<GetErrorListResponse, Error>({
    queryKey: ['getErrorList', payload],
    queryFn: () => getErrorList(payload),
    enabled,
  });
};

export const useExportErrorList = (
  onSuccess?: (data: ExportErrorListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExportErrorListResponse,
    Error,
    ExportErrorListRequestParams
  >({
    mutationFn: (params) => exportErrorList(params),
    mutationKey: ['exportErrorList'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReProductionErrorList = (
  payload: GetErrorListRequestPayload,
  enabled: boolean = false
) => {
  return useQuery<GetErrorListResponse, Error>({
    queryKey: ['getReProductionErrorList', payload],
    queryFn: () => getReProductionErrorList(payload),
    enabled,
  });
};

export const useExportReProductionErrorList = (
  onSuccess?: (data: ExportErrorListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExportErrorListResponse,
    Error,
    ExportErrorListRequestParams
  >({
    mutationFn: (params) => exportReProductionErrorList(params),
    mutationKey: ['exportReProductionErrorList'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProductLocationView = (
  params: GetProductLocationViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetProductLocationViewResponse, Error>({
    queryKey: ['getProductLocationView', params],
    queryFn: () => getProductLocationView(params),
    enabled,
  });
};

export const useValidateDestination = (
  onSuccess?: (data: ValidateDestinationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ValidateDestinationResponse,
    Error,
    ValidateDestinationRequestParams
  >({
    mutationFn: (params) => validateDestination(params),
    mutationKey: ['validateDestination'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateDestination = (
  onSuccess?: (data: CreateDestinationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    CreateDestinationResponse,
    Error,
    CreateDestinationRequestBody
  >({
    mutationFn: (body) => createDestination(body),
    mutationKey: ['createDestination'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveProductionLocation = (
  onSuccess?: (data: SaveProductionLocationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveProductionLocationResponse,
    Error,
    SaveProductionLocationRequestBody
  >({
    mutationFn: (body) => saveProductionLocation(body),
    mutationKey: ['saveProductionLocation'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetSummaryView = (
  params: GetSummaryViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetSummaryViewResponse, Error>({
    queryKey: ['getSummaryView', params],
    queryFn: () => getSummaryView(params),
    enabled,
  });
};

export const useSaveSummary = (
  onSuccess?: (data: SaveSummaryResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<SaveSummaryResponse, Error, SaveSummaryRequestBody>({
    mutationFn: (body) => saveSummary(body),
    mutationKey: ['saveSummary'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetGenerateView = (
  params: GetGenerateViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetGenerateViewResponse, Error>({
    queryKey: ['getGenerateView', params],
    queryFn: () => getGenerateView(params),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export const useGetGenerateStatus = (
  params: GetGenerateStatusRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetGenerateStatusResponse, Error>({
    queryKey: ['getGenerateStatus', params],
    queryFn: () => getGenerateStatus(params),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export const useGetExportBatesRange = (
  params: GetExportBatesRangeRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetExportBatesRangeResponse, Error>({
    queryKey: ['getExportBatesRange', params],
    queryFn: () => getExportBatesRange(params),
    enabled,
  });
};

export const useExecuteGenerate = (
  onSuccess?: (data: ExecuteGenerateResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExecuteGenerateResponse,
    Error,
    ExecuteGenerateRequestBody
  >({
    mutationFn: (body) => executeGenerate(body),
    mutationKey: ['executeGenerate'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useExecutePlaceholdering = (
  onSuccess?: (data: ExecutePlaceholderingResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExecutePlaceholderingResponse,
    Error,
    ExecutePlaceholderingRequestBody
  >({
    mutationFn: (body) => executePlaceholdering(body),
    mutationKey: ['executePlaceholdering'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useConfirmProduction = (
  onSuccess?: (data: ConfirmProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ConfirmProductionResponse,
    Error,
    ConfirmProductionRequestParams
  >({
    mutationFn: confirmProduction,
    mutationKey: ['confirmProduction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetShareableLinks = (
  onSuccess?: (data: GetShareableLinksResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GetShareableLinksResponse,
    Error,
    GetShareableLinksRequestParams
  >({
    mutationFn: getShareableLinks,
    mutationKey: ['getShareableLinks'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetConfirmView = (
  params: GetConfirmViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetConfirmViewResponse, Error>({
    queryKey: ['getConfirmView', params],
    queryFn: () => getConfirmView(params),
    enabled,
  });
};

export const useGetProductionPreview = (
  params: GetProductionPreviewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetProductionPreviewResponse, Error>({
    queryKey: ['getProductionPreview', params],
    queryFn: () => getProductionPreview(params),
    enabled,
    retry: 2,
  });
};

export const useGetProductionPreviewFile = (
  onSuccess?: (data: GetProductionPreviewFileResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GetProductionPreviewFileResponse,
    Error,
    GetProductionPreviewFileRequestParam
  >({
    mutationFn: (params) => getProductionPreviewFile(params),
    mutationKey: ['getProductionPreviewFile'],
    onSuccess: (data) => {
      // Create download link and trigger download
      const url = window.URL.createObjectURL(data.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUploadSeedFile = (
  onSuccess?: (data: UploadSeedFileResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<UploadSeedFileResponse, Error, File>({
    mutationFn: uploadSeedFile,
    mutationKey: ['uploadSeedFile'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUploadReProductionSeedFile = (
  onSuccess?: (data: UploadSeedFileResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<UploadSeedFileResponse, Error, File>({
    mutationFn: uploadReProductionSeedFile,
    mutationKey: ['uploadReProductionSeedFile'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useValidateDownloadURI = (
  params: ValidateDownloadURIRequest,
  enabled: boolean = true
) => {
  return useQuery<ValidateDownloadURIResponse, Error>({
    queryKey: ['getValidateDownloadURI', params],
    queryFn: () => getValidateDownloadURI(params),
    enabled,
    retry: 0,
  });
};

export const useValidateDownload = (
  onSuccess?: (data: ValidateDownloadResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ValidateDownloadResponse,
    Error,
    {payload: ValidateDownloadRequest; queryParam?: string}
  >({
    mutationFn: ({payload, queryParam}) =>
      validateDownload(payload, queryParam),
    mutationKey: ['validateDownload'],
    onSuccess: (data) => {
      // Create download link and trigger download
      const url = window.URL.createObjectURL(data.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetSeedPath = (
  params: GetSeedPathRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetSeedPathResponse, Error>({
    queryKey: ['getSeedPath', params],
    queryFn: () => getSeedPath(params),
    enabled,
    retry: 2,
  });
};

export const useGetReProductionSeedPath = (
  params: GetSeedPathRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetSeedPathResponse, Error>({
    queryKey: ['getReProductionSeedPath', params],
    queryFn: () => getReProductionSeedPath(params),
    enabled,
    retry: 2,
  });
};

export const useSeedPageCount = (
  onSuccess?: (data: SeedPageCountResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SeedPageCountResponse,
    Error,
    {payload: SeedPageCountRequest; queryParam?: string}
  >({
    mutationFn: ({payload, queryParam}) => seedPageCount(payload, queryParam),
    mutationKey: ['seedPageCount'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useReProductionSeedPageCount = (
  onSuccess?: (data: SeedPageCountResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SeedPageCountResponse,
    Error,
    {payload: SeedPageCountRequest; queryParam?: string}
  >({
    mutationFn: ({payload, queryParam}) =>
      reProductionSeedPageCount(payload, queryParam),
    mutationKey: ['reProductionSeedPageCount'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useConfirmReProduction = (
  onSuccess?: (data: ConfirmProductionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ConfirmProductionResponse,
    Error,
    ConfirmProductionRequestParams
  >({
    mutationFn: confirmReProduction,
    mutationKey: ['confirmReProduction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetConfirmViewForReProduction = (
  params: GetConfirmViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetReProductionConfirmViewResponse, Error>({
    queryKey: ['getReProductionConfirmView', params],
    queryFn: () => getReProductionConfirmView(params),
    enabled,
  });
};
